import { useEffect, useState } from 'react';
import { Image, Form, Button, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import logo from './logo.svg';
import infoIcon from "./info.svg";
import MailchimpSignup from './MailchimpSignup';

const ProjectLoader = ({setProject, isLoading}) => {

  const projects = [
    {
      name: "Unit testing",
      deps: [], 
      otherScripts: [
        '/python/project8/sample/__init__.py',
        '/python/project8/sample/helpers.py',
        '/python/project8/sample/core.py',
        '/python/project8/tests/__init__.py',
      ], 
      entryScript: '/python/project8/tests/test_sample.py'
    },
    {name: "Machine Learning", deps: ['scikit-learn'], entryScript: './python/script9.py', ignoreWarnings: true},
    {name: "Basic calculations", deps: [], entryScript: './python/script1.py'},
    {name: "Download artifacts", deps: [], entryScript: './python/script3.py', artifacts: ['english_result.txt', 'french_result.txt']},
    {name: "Import numpy dependency", deps: ['numpy'], entryScript: './python/script4.py'},
    {name: "Import snowballstemmer dependency", deps: ['snowballstemmer'], entryScript: './python/script6.py'},
    {
      name: "Multi-file project",
      deps: [], 
      otherScripts: [
        '/python/project7/sample/__init__.py', 
        '/python/project7/sample/helpers.py',
        '/python/project7/sample/core.py'
      ], 
      entryScript: '/python/project7/script.py'
    },
  ];

  const emptyProject = {
    name: "Custom Project", deps: [], otherScripts: [], entryScript: "", artifacts: []
  };

  const [projectIdx, setProjectIdx] = useState(0);
  const [customProject, setCustomProject] = useState(emptyProject);
  const [filesLoaded, setFilesLoaded] = useState([]);
  const [entryScript, setEntryScript] = useState({});
  const {acceptedFiles, getRootProps, getInputProps} = useDropzone();

  useEffect(() => {
    setProject(projects[0]);
  }, // eslint-disable-next-line 
  [])

  useEffect(() => {
    let otherScripts = [];
    let loaded = [];
    for(let i=0; i<acceptedFiles.length; i++)
      loaded[i] = false;

    acceptedFiles.forEach((file, idx) => {
      const reader = new FileReader();
      reader.addEventListener('load', (e) => {
        otherScripts[idx] = {path: file.path, data: e.target.result};
        loaded[idx] = true;
        if(loaded.reduce((p, loadState) => p && loadState))
        {
          setFilesLoaded(true);
          setCustomProject({...customProject, otherScripts: otherScripts});
        }
        else
        {
          setFilesLoaded(false);
        }
      });
      reader.readAsText(file);
    })
  }, // eslint-disable-next-line
  [acceptedFiles])

  useEffect(() => {
    for(let i=0; i<customProject.otherScripts.length; i++)
    {
      if(customProject.otherScripts[i].path === entryScript)
      {
        setCustomProject({...customProject, entryScript: customProject.otherScripts[i]});
        break;
      }
    }
  }, // eslint-disable-next-line 
  [entryScript, filesLoaded])

  const versionString = process.env.REACT_APP_PYODIDE_VERSION.replace(/\./g,'-');
  const pyodideChangelogLink = `https://pyodide.org/en/stable/project/changelog.html#version-${versionString}`;

  return (
    <>
      <Image src={logo} className="App-logo" alt="logo" />
      <h2>Python</h2>
      <OverlayTrigger
        placement="right"
        transition={false}
        delay={{ show: 100, hide: 2000 }}
        overlay={
          <Tooltip>
            Runs on&nbsp;
            <a href={pyodideChangelogLink} target="_blank" rel="noreferrer">
              Pyodide v{process.env.REACT_APP_PYODIDE_VERSION}
            </a>
          </Tooltip>
        }
      >
        <Image src={infoIcon} alt="info" />
      </OverlayTrigger>
      <hr/>
      <Form>
        <Form.Group controlId="projectForm.projectSelect">
          <Form.Label>Select an example</Form.Label>
          <Form.Control as="select" onChange={(e) => setProjectIdx(e.target.value)}>
            {projects.map((proj, idx) => <option key={idx} value={idx}>{proj.name}</option>)}
            <option value={-1}>Try your own!</option>
          </Form.Control>
        </Form.Group>
        {// eslint-disable-next-line
        projectIdx == -1 ?
        <>
          <div {...getRootProps({className: 'dropzone'})} style={{borderStyle: "dashed"}}>
            <input {...getInputProps()} />
            <br/><br/><br/>
            <p><b>Drag 'n' drop your project folder here</b><br/>(we don't upload anything, all code stays in your browser)</p>
            <br/><br/><br/>
          </div>
          {acceptedFiles.length > 0 &&
          <>
            <Form.Group controlId="projectForm.entryScript">
              <Form.Label>Choose script to run</Form.Label>
              <Form.Control defaultValue={""} as="select" onChange={(e) => setEntryScript(e.target.value)}>
                <option value={""}>Please select a script</option>
                {acceptedFiles.filter((file) => file.path.endsWith(".py") && !file.path.endsWith("__init__.py")).map((file) => {
                  return <option key={file.path} value={file.path}>{file.path}</option>
                })}
              </Form.Control>
            </Form.Group>
            <Form.Group controlId="projectForm.dependencies">
              <Form.Label>Comma separated list of modules (e.g. numpy, snowballstemmer, etc...)</Form.Label>
              <Form.Control as="textarea" rows={1} onChange={(e) => {
                if(e.target.value.trim().length  === 0)
                {
                  setCustomProject({...customProject, deps: []});
                  return;
                }

                const depsWithWhitespace = e.target.value.split(',');
                const deps = depsWithWhitespace.map((dep) => dep.trim());
                setCustomProject({...customProject, deps: deps});
              }}/>
            </Form.Group>
            <Form.Group controlId="projectForm.dependencies">
              <Form.Label>Comma separated list of artifact paths (e.g. /result/artifact.txt, file.txt, etc...)</Form.Label>
              <Form.Control as="textarea" rows={3} onChange={(e) => {
                const artifactsWithWhitespace = e.target.value.split(',');
                const artifacts = artifactsWithWhitespace.map((artifact) => {
                  const nameNoWhitespace = artifact.trim();
                  if(nameNoWhitespace.startsWith('./'))
                    return nameNoWhitespace.substring(1);
                  else
                    return nameNoWhitespace;
                });
                setCustomProject({...customProject, artifacts: artifacts});
              }}/>
            </Form.Group>
            <Button className="rkn-primary-btn" disabled={!filesLoaded || !customProject.entryScript || isLoading} onClick={()=>{setProject(customProject)}}>Run</Button>
          </>
          }
        </>
        :
        <>
          <Button 
            className="rkn-primary-btn" 
            disabled={isLoading} 
            onClick={()=>
            {
              setProject(projects[projectIdx]);
              setCustomProject(emptyProject);
            }}
          >
            Run
          </Button>
        </>
        }
        <hr/>
        {/* <MailchimpSignup/> */}
      </Form>
    </>
  )
}

export default ProjectLoader;